// frontend/src/components/homepage/NumbersLive.js
//
// "המספרים" - three stats that count up from 0 when they scroll into view.
//
// THE COUNT-UP IS CONTENT, SO ITS FINAL VALUE IS THE DEFAULT. A count-up that
// never starts (no scroll, no observer, motion off, the prerenderer) must
// leave the real number in the DOM, not a "0" - the prerender snapshot is what
// crawlers read, and "0+ posts scanned" is a claim we do not make. So:
//   * motion off, or window.__PRERENDER__  -> the final value on the FIRST render;
//   * otherwise                            -> "0" until the stat is half visible
//                                             (useInViewGate threshold .5), then
//                                             2000ms of ease-out quart through the
//                                             shared rAF chain, once.
//
// AND IT ALWAYS LANDS. Once begun the count owns itself: the gate is `once`
// (scrolling back out cannot cancel it) and the effect's cleanup snaps the
// stat to its final value if anything else ever tears the run down. A number
// stranded mid-count is a wrong claim on the page, not a missing animation.
//
// LOCALE IS PINNED. `toLocaleString("en-US")`, never the bare call: the CI
// runner's locale differs from a dev machine's and the digit grouping with it,
// and a number that renders "500,000" here and "500 000" there is a test that
// passes for the wrong reason.

import React, { useEffect, useRef, useState } from "react";
import SectionShell from "./SectionShell";
import useReveal from "../../motion/useReveal";
import useMotion from "../../motion/useMotion";
import useInViewGate, { isPrerendering } from "../../motion/useInViewGate";
import useHydrated from "../../motion/useHydrated";
import { addTask } from "../../motion/rafCoordinator";
import "../../homepage/typesNumbersCta.css";

/** The prototype's `dur=2000`. */
export const COUNT_UP_MS = 2000;

export const COPY = {
  he: {
    heading: "המספרים מדברים בעד עצמם",
    stats: [
      // ROW 1 - what the fleet does.
      {
        end: 500000,
        sep: true,
        unit: "+",
        label: "פוסטים נסרקים מדי חודש",
        // Phones only (see labelShort below): the full label is three lines in
        // a 105px column while its two neighbours are one, which drops this
        // tile's caption out of line with theirs.
        labelShort: "פוסטים בחודש",
        desc: "רק הרלוונטיים נשלחים אליך",
      },
      {
        end: 100,
        sep: false,
        unit: "%",
        label: "מהפוסטים עוברים ניתוח AI",
        labelShort: "עוברים ניתוח AI",
        desc: "לא סינון לפי מילות מפתח",
      },
      { end: 80, sep: false, unit: "+", label: "שעות עבודה", desc: "שה-AI שלנו חוסך לך בחודש" },
      // ROW 2 - how fast it does it. `unitWord` is what tells the renderer the
      // unit is a WORD rather than the "+" / "%" / "/7" glyph suffix: it is set
      // in the page's own direction (so Hebrew reads number-then-word), in the
      // UI font (JetBrains Mono has no Hebrew glyphs at all) and at a fraction
      // of the figure's size, so the numeral stays the thing you read first.
      {
        end: 5,
        sep: false,
        // The tilde is a PREFIX, not part of the figure: it renders in the
        // unit's accent colour, sits before the digit in reading order, and
        // the counter never touches it (it animates the digit alone).
        prefix: "~",
        unit: "דקות",
        unitWord: true,
        label: "מהפרסום ועד הזיהוי",
        desc: "אתם מגיעים ללקוח לפני המתחרים",
      },
      {
        end: 30,
        sep: false,
        unit: "שניות",
        unitWord: true,
        label: "מהזיהוי ועד ההתראה",
        desc: "ישירות לטלפון",
      },
    ],
  },
  en: {
    heading: "The numbers speak for themselves",
    stats: [
      {
        end: 500000,
        sep: true,
        unit: "+",
        label: "Posts scanned per month",
        labelShort: "Posts per month",
        desc: "only the relevant ones are sent to you",
      },
      {
        end: 100,
        sep: false,
        unit: "%",
        label: "of posts go through AI analysis",
        labelShort: "AI analysis",
        desc: "not a keyword filter",
      },
      { end: 80, sep: false, unit: "+", label: "Work hours", desc: "our AI saves you every month" },
      {
        end: 5,
        sep: false,
        prefix: "~",
        unit: "min",
        unitWord: true,
        label: "from post to detection",
        desc: "you reach the customer before your competitors",
      },
      { end: 30, sep: false, unit: "sec", unitWord: true, label: "from detection to alert", desc: "straight to your phone" },
    ],
  },
};

/** How many tiles the first row carries; the rest go in the second. */
export const ROW_ONE_COUNT = 3;

/** Reveal stagger per stat, the prototype's `--d` values, extended to five. */
const DELAYS = ["0s", ".1s", ".2s", ".3s", ".4s"];

/**
 * The prototype's `fmt`: round, then group with an EXPLICIT locale when the
 * stat asks for separators.
 *
 * @param {number} value
 * @param {boolean} sep
 * @returns {string}
 */
export function formatCount(value, sep) {
  const n = Math.round(value);
  return sep ? n.toLocaleString("en-US") : String(n);
}

/** The prototype's easing: `1 - (1 - p)^4`. */
function easeOutQuart(p) {
  return 1 - Math.pow(1 - p, 4);
}

/**
 * @param {number} end
 * @param {boolean} sep
 * @param {boolean} active true once the stat is in view.
 * @returns {string} the text to render right now.
 */
function useCountUp(end, sep, active) {
  const { off } = useMotion();
  const finalText = formatCount(end, sep);
  const settled = off || isPrerendering();
  // Declared FIRST so its cleanup runs FIRST at unmount (React runs a
  // component's effect cleanups in declaration order): the count effect below
  // reads it to tell "the gate went away" from "the component went away".
  const mountedRef = useRef(true);
  useEffect(() => {
    // SET as well as clear: StrictMode mounts, unmounts and REMOUNTS in dev,
    // and a ref that is only ever cleared stays false for the second mount -
    // which would silently disable the snap below exactly where it is being
    // developed.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // The prerendered snapshot shows the finished figure; the hydrating
  // client's first render must match it (the count then starts from 0 once
  // the stat is in view, as it always did after the snapshot was replaced).
  const hydrated = useHydrated();
  const [text, setText] = useState(() =>
    settled || !hydrated ? finalText : formatCount(0, sep)
  );
  // The prototype unobserves after the first intersection: one run, ever.
  const startedRef = useRef(settled);

  useEffect(() => {
    // Motion off (at mount OR mid-count) and the prerenderer both mean "show
    // the number". Checked before the once-guard so a count interrupted by the
    // toggle lands on its final value instead of freezing mid-way.
    if (off || isPrerendering()) {
      startedRef.current = true;
      setText(finalText);
      return undefined;
    }
    if (startedRef.current || !active) return undefined;
    startedRef.current = true;

    let t0 = null;
    let finished = false;
    let remove = () => {};
    const task = (dtMs, nowMs) => {
      if (t0 === null) t0 = nowMs;
      const p = Math.max(0, Math.min(1, (nowMs - t0) / COUNT_UP_MS));
      setText(formatCount(end * easeOutQuart(p), sep));
      if (p >= 1) {
        finished = true;
        remove();
      }
    };
    remove = addTask(task);
    // A count torn down mid-flight must LAND, never freeze: the number is
    // content, and "99,923+" is a claim we do not make either. The cleanup
    // drops the frame task (it must - an orphaned task would setState into a
    // dead component every frame), so whatever removes the task owes the
    // reader the final value. Skipped on unmount, where there is nothing left
    // to show it to. See the once-gate note above for the case this backstops.
    return () => {
      remove();
      if (!finished && mountedRef.current) setText(finalText);
    };
  }, [active, off, end, sep, finalText]);

  return text;
}

function Stat({ stat, delay }) {
  const reveal = useReveal();
  // The gate sits on the NUMBER, as the prototype observes `[data-count]`, at
  // its threshold .5.
  //
  // `once` IS THE FIX FOR A FREEZE, not a perf tweak (2026-09-06; the bug is
  // as old as the section). Without it the gate is a live toggle: scrolling the
  // section back out mid-count flipped `inView` to false, which re-ran the
  // effect below, and its CLEANUP pulled the frame task off the chain. The
  // once-guard (`startedRef`) then refused to start a second run, so the stat
  // stayed on whatever frame it died on - the owner saw "4/7", "6+",
  // "15,665+" - for the rest of the session. The prototype unobserves after
  // the first intersection for exactly this reason: the count is one run,
  // ever, and once it has begun the viewport has no further say in it.
  const [gateRef, inView] = useInViewGate({ threshold: 0.5, once: true });
  const text = useCountUp(stat.end, stat.sep, inView);

  return (
    <div
      ref={reveal.ref}
      className={reveal.className ? `hl-nm-stat ${reveal.className}` : "hl-nm-stat"}
      style={{ "--hl-d": delay }}
    >
      <b
        className={stat.unitWord ? "hl-nm-val hl-nm-val-word" : "hl-nm-val"}
        ref={gateRef}
      >
        {stat.prefix ? <span className="hl-nm-prefix">{stat.prefix}</span> : null}
        <span className="hl-nm-num">{text}</span>
        {/* The space is INSIDE the unit span, not a JSX gap: a word unit needs
            one and a glyph unit ("+", "%", "/7") must not have one, and a
            literal here is the only spacing HTML will not collapse away. */}
        <span className={stat.unitWord ? "hl-nm-unit hl-nm-unit-word" : "hl-nm-unit"}>
          {stat.unitWord ? ` ${stat.unit}` : stat.unit}
        </span>
      </b>
      {/* Both labels are in the DOM and CSS picks one by width. A React-side
          media read cannot be used here: the prerenderer runs at 800x600 (a
          phone width) and the desktop visitor hydrating that snapshot would
          render the other string - a hydration mismatch on real content. */}
      <span className="hl-nm-label">
        {stat.labelShort ? (
          <>
            <span className="hl-nm-label-full">{stat.label}</span>
            <span className="hl-nm-label-short">{stat.labelShort}</span>
          </>
        ) : (
          stat.label
        )}
      </span>
      <span className="hl-nm-desc">{stat.desc}</span>
    </div>
  );
}

export default function NumbersLive({ isHebrew = true }) {
  const t = isHebrew ? COPY.he : COPY.en;
  const head = useReveal();

  return (
    <SectionShell
      id="numbers"
      scene="numbers"
      mesh="core"
      meshAlpha=".9"
      className="hl-nm"
      // Owner round 2: the bottom padding is cut to 64px (48px on phones) so
      // the proof and the CTA below read as one beat. `pb` after `py`
      // overrides only the bottom - the CtaLive pattern.
      // Owner round 3: the whole page rhythm drops 20%, and a section that
      // sets its own padding has to be scaled by hand or it grows relative to
      // its neighbours (140 -> 112, 64 -> 51, 48 -> 38).
      sx={{ py: "112px", pb: { xs: "38px", md: "51px" } }}
    >
      <h2
        ref={head.ref}
        className={
          head.className
            ? `hl-h2 hl-h2-center hl-nm-h2 ${head.className}`
            : "hl-h2 hl-h2-center hl-nm-h2"
        }
      >
        {t.heading}
      </h2>
      {/* TWO grids, not one wrapping grid. Each row owns its own columns, so
          the 3-up and the 2-up keep their own track widths, and the vertical
          hairline (`.hl-nm-stat + .hl-nm-stat`) can only ever be drawn between
          tiles of the SAME row - which is what a wrapped single grid could not
          promise. The pair below is centred by being two thirds as wide, so a
          row-2 tile is exactly as wide as a row-1 tile. */}
      <div className="hl-nm-rows">
        <div className="hl-nm-stats hl-nm-stats-3">
          {t.stats.slice(0, ROW_ONE_COUNT).map((stat, i) => (
            <Stat key={stat.label} stat={stat} delay={DELAYS[i]} />
          ))}
        </div>
        <div className="hl-nm-stats hl-nm-stats-2">
          {t.stats.slice(ROW_ONE_COUNT).map((stat, i) => (
            <Stat key={stat.label} stat={stat} delay={DELAYS[ROW_ONE_COUNT + i]} />
          ))}
        </div>
      </div>
    </SectionShell>
  );
}
